import api from './apiClient';

export interface GuestInfo {
  name: string;
  cedula?: string;
  email?: string;
  companyName?: string;
  reason?: string;
  vehiclePlate?: string;
  registeredUser?: string;
}

export interface CheckIn {
  guestIndex: number;
  checkedInAt: string;
  checkedOutAt?: string;
  checkedInBy: { _id: string; firstName: string; lastName: string; email: string } | string;
}

export interface GuestVisit {
  _id: string;
  code: string;
  company: { _id: string; name: string } | string;
  registeredBy: { _id: string; firstName: string; lastName: string; email: string } | string;
  guests: GuestInfo[];
  startDate: string;
  endDate: string;
  /**
   * Optional DAILY time-of-day access window ("HH:mm", 24h), e.g. guests
   * may only be let in between accessStartTime and accessEndTime each day
   * of the visit. Distinct from startDate/endDate (the overall visit
   * range). Both set together or neither. Informational only — not
   * enforced at check-in/out.
   */
  accessStartTime?: string;
  accessEndTime?: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  checkIns: CheckIn[];
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GuestVisitSearchResult extends GuestVisit {
  matchingGuestIndexes: number[];
}

interface CreateGuestVisitData {
  guests: Array<{
    name: string;
    cedula?: string;
    email?: string;
    companyName?: string;
    reason?: string;
    vehiclePlate?: string;
    registeredUser?: string;
  }>;
  startDate: string;
  endDate: string;
  notes?: string;
  companyId?: string;
  accessStartTime?: string;
  accessEndTime?: string;
}

class GuestVisitApi {
  async create(data: CreateGuestVisitData): Promise<GuestVisit> {
    const result = await api.post('/guest-visits', data);
    return result.data;
  }

  async getAll(params?: {
    page?: number;
    limit?: number;
    status?: string;
    company?: string;
  }): Promise<{ data: GuestVisit[]; pagination: any }> {
    const queryParams: Record<string, any> = {};
    if (params?.page) queryParams.page = String(params.page);
    if (params?.limit) queryParams.limit = String(params.limit);
    if (params?.status) queryParams.status = params.status;
    if (params?.company) queryParams.company = params.company;

    return await api.get('/guest-visits', queryParams);
  }

  async getById(id: string): Promise<GuestVisit> {
    const result = await api.get(`/guest-visits/${id}`);
    return result.data;
  }

  /**
   * Edit a scheduled visit. Server-gated: admins/company_admin only,
   * scheduled status only, and only while the start is >24h away.
   */
  async update(
    id: string,
    data: Omit<CreateGuestVisitData, 'companyId'>,
  ): Promise<GuestVisit> {
    const result = await api.patch(`/guest-visits/${id}`, data);
    return result.data;
  }

  async searchByCedula(cedula: string, date?: string): Promise<GuestVisitSearchResult[]> {
    const queryParams: Record<string, any> = { cedula };
    if (date) queryParams.date = date;
    const result = await api.get('/guest-visits/search', queryParams);
    return result.data;
  }

  async checkIn(id: string, guestIndex: number): Promise<GuestVisit> {
    const result = await api.patch(`/guest-visits/${id}/check-in`, { guestIndex });
    return result.data;
  }

  async checkOut(id: string, guestIndex: number): Promise<GuestVisit> {
    const result = await api.patch(`/guest-visits/${id}/check-out`, { guestIndex });
    return result.data;
  }

  /** Registra entrada para todos los invitados que aún no han hecho check-in. */
  async checkInAll(id: string): Promise<GuestVisit> {
    const result = await api.patch(`/guest-visits/${id}/check-in-all`, {});
    return result.data;
  }

  /** Registra salida para todos los invitados con entrada activa. */
  async checkOutAll(id: string): Promise<GuestVisit> {
    const result = await api.patch(`/guest-visits/${id}/check-out-all`, {});
    return result.data;
  }

  async updateStatus(id: string, status: string): Promise<GuestVisit> {
    const result = await api.patch(`/guest-visits/${id}/status`, { status });
    return result.data;
  }

  async verifyByCode(code: string): Promise<{
    data: GuestVisit;
    verification: { isActive: boolean; isExpired: boolean; isCancelled: boolean; isValid: boolean };
  }> {
    return await api.get(`/guest-visits/verify/${code}`);
  }

  async sendEmail(id: string, guestIndex: number, email: string): Promise<void> {
    await api.post(`/guest-visits/${id}/send-email`, { guestIndex, email });
  }

  /** Reenvía la invitación a todos los invitados pendientes que tengan correo. */
  async sendReminderEmails(id: string): Promise<void> {
    await api.post(`/guest-visits/${id}/send-reminder-emails`, {});
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/guest-visits/${id}`);
  }
}

export const guestVisitApi = new GuestVisitApi();
